import React, { FunctionComponent, PropsWithChildren, useEffect, useRef, useState } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';
import { Spin } from 'antd';
import { AppStore, userActions } from '@credistream/shared';
import { useQuery } from '~/hooks/useQuery';
import { useAppDispatch } from '~/initializeStore';

const TOKEN_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

interface OwnProps {}

type Props = PropsWithChildren<OwnProps>;

const AuthChecker: FunctionComponent<Props> = ({ children }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [isUserResolved, setIsUserResolved] = useState(false);
  const user = useSelector((store: AppStore) => store.user);
  const userUid = user.data?.uid;
  const query = useQuery();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleUserData = async (newUser: firebase.User | null) => {
    if (newUser && newUser.uid !== userUid) {
      dispatch(userActions.setLoggedUserData(newUser));
    }
    if (history.location.pathname.startsWith('/auth') && newUser) {
      const continuePath = query.get('continue');
      history.push(continuePath || '/home');
    }
    if (!isUserResolved) {
      setTimeout(() => {
        setIsUserResolved(true);
      }, 0);
    }
  };

  const debouncedCallback = useDebouncedCallback(handleUserData, 400);

  useEffect(() => {
    const unsubscribe = firebase.auth().onIdTokenChanged(debouncedCallback);

    return () => unsubscribe();
  }, [debouncedCallback, isUserResolved, userUid]);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      const currentUser = firebase.auth().currentUser;
      if (currentUser) {
        currentUser.getIdToken(true).catch(() => {
          firebase.auth().signOut();
        });
      }
    }, TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  if (!isUserResolved) {
    return (
      <div
        style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthChecker;
